import type { StatusDefinition } from "@kontextwerk/web-sdk/core/display/status"
import {
  mdiCalendarCheckOutline,
  mdiCalendarClockOutline,
  mdiClockOutline,
  mdiCloseCircleOutline,
  mdiEyeCheckOutline,
  mdiProgressClock,
} from "@mdi/js"

export const scheduleStatusDefinitions: Record<
  ScheduleEventStatus,
  StatusDefinition<ScheduleEventStatus>
> = {
  RESERVED: {
    value: "RESERVED",
    label: "Ankündigung offen",
    color: "blue",
    icon: mdiProgressClock,
    description: "Termin wurde noch nicht sicher angekündigt.",
  },
  ANNOUNCED: {
    value: "ANNOUNCED",
    label: "Angekündigt",
    color: "forest",
    icon: mdiEyeCheckOutline,
    description: "Der Kunde wurde über den Termin informiert, hat ihn aber noch nicht bestätigt.",
  },
  CONFIRMED: {
    value: "CONFIRMED",
    label: "bestätigt",
    color: "emerald",
    icon: mdiCalendarCheckOutline,
    description: "Termin wurde vom Kunden bestätigt.",
  },
  COMPLETED: {
    value: "COMPLETED",
    label: "durchgeführt",
    color: "neutral",
    icon: mdiCalendarCheckOutline,
    description: "Termin wurde durchgeführt.",
  },
  CANCELED: {
    value: "CANCELED",
    label: "abgesagt",
    color: "neutral",
    icon: mdiCloseCircleOutline,
    description: "Termin wurde abgesagt.",
  },
  NOSHOW: {
    value: "NOSHOW",
    label: "nicht stattgefunden",
    color: "rose",
    icon: mdiClockOutline,
    description:
      "Termin galt bereits als erledigt, wurde aber manuell als nicht stattgefunden markiert.",
  },
}

export const scheduleStatusLabels: Record<ScheduleEventStatus, string> = Object.fromEntries(
  Object.entries(scheduleStatusDefinitions).map(([value, def]) => [value, def.label]),
) as Record<ScheduleEventStatus, string>

const normalizeScheduleStatus = (status?: unknown) =>
  String(status ?? "")
    .trim()
    .toUpperCase()

export const displayScheduleStatusLabel = (status?: unknown): string => {
  const normalized = normalizeScheduleStatus(status)

  if (!normalized) return "Terminstatus unbekannt"

  return scheduleStatusLabels[normalized as ScheduleEventStatus] ?? "Terminstatus unbekannt"
}

const defaultHiddenScheduleStatuses = new Set<ScheduleEventStatus>(["CANCELED", "NOSHOW"])

export const defaultVisibleScheduleStatuses = (
  statuses: ScheduleEventStatus[],
): ScheduleEventStatus[] => statuses.filter((status) => !defaultHiddenScheduleStatuses.has(status))

export const resolveScheduleStatusDefinition = (
  status?: ScheduleEventStatus | null,
  overrideLabel?: string | null,
): StatusDefinition<ScheduleEventStatus | string> | null => {
  if (!status) return null

  const base = scheduleStatusDefinitions[status]

  if (base) return overrideLabel ? { ...base, label: overrideLabel } : base

  if (!overrideLabel) return null

  return {
    value: status,
    label: overrideLabel,
    color: "blue",
    icon: mdiCalendarClockOutline,
  }
}

const customerCommunicatedStatuses = new Set<ScheduleEventStatus>([
  "ANNOUNCED",
  "CONFIRMED",
  "COMPLETED",
])

export const isCustomerCommunicatedScheduleStatus = (
  status?: ScheduleEventStatus | null,
): boolean => Boolean(status && customerCommunicatedStatuses.has(status))

export const resolveScheduleEventEditLockNotice = ({
  status,
  hasContactOrder = false,
}: {
  status?: ScheduleEventStatus | null
  hasContactOrder?: boolean
}): string | null => {
  if (!hasContactOrder && !isCustomerCommunicatedScheduleStatus(status)) return null

  return "Dieser Termin wurde bereits an den Kunden gesendet. Bitte nutzen Sie „Termin verschieben“, damit die Kundenkommunikation sauber bleibt."
}
